import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { Music24PlanTier } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../common/types';
import { teamPlanLabel } from './teamAcceptMessages';
import { Panel } from '../music24/components/primitives/Panel';
import styles from './TeamAcceptWorkspaceRow.module.scss';

interface TeamAcceptWorkspaceRowProps {
  workspaceName: string;
  plan: Music24PlanTier;
  artistsCount: number;
  seatsUsed: number;
  seatsLimit: number | null;
  /** c6: the seat count shows in the danger color. */
  seatsFull?: boolean;
  /** c5: the caller's role chip replaces the seat count. */
  roleLabel?: string;
}

/** The first letters of the first two words: "Nightshift Records" → "NR". */
function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

/**
 * The workspace row of design workspaces c: its name, then "Label plan · 12 artists · 4 of 10 seats"
 * (the seats in red on c6). On c5 a role chip replaces the seats: "Label plan · 12 artists  MEMBER".
 */
const TeamAcceptWorkspaceRowComponent: React.FC<TeamAcceptWorkspaceRowProps> = ({
  workspaceName,
  plan,
  artistsCount,
  seatsUsed,
  seatsLimit,
  seatsFull = false,
  roleLabel,
}) => {
  const intl = useIntl();
  const initials = useMemo(() => initialsOf(workspaceName), [workspaceName]);

  const artists = intl.formatMessage(
    {
      id: 'app.sml.team.accept.workspace.artists',
      defaultMessage: '{count, plural, one {# artist} other {# artists}}',
    },
    { count: artistsCount }
  );
  const seats =
    seatsLimit === null
      ? intl.formatMessage(
          {
            id: 'app.sml.team.accept.workspace.seatsUnlimited',
            defaultMessage: '{used, plural, one {# seat} other {# seats}} used',
          },
          { used: seatsUsed }
        )
      : intl.formatMessage(
          { id: 'app.sml.team.accept.workspace.seats', defaultMessage: '{used} of {limit} seats' },
          { used: seatsUsed, limit: seatsLimit }
        );

  return (
    <Panel nested className="d-flex align-items-center p-3">
      <div
        className={`d-flex align-items-center justify-content-center fw-bold me-3 ${styles.avatar}`}
        aria-hidden="true"
      >
        {initials}
      </div>
      <div className="text-break flex-grow-1">
        <div className="fw-bold">{workspaceName}</div>
        {roleLabel === undefined ? (
          <small className={styles.workspaceMeta}>
            {`${teamPlanLabel(intl, plan)} · ${artists} · `}
            <span className={seatsFull ? styles.seatsFull : undefined}>{seats}</span>
          </small>
        ) : (
          <small className={styles.workspaceMeta}>{`${teamPlanLabel(intl, plan)} · ${artists}`}</small>
        )}
      </div>
      {roleLabel !== undefined && <span className={`ms-2 ${styles.role}`}>{roleLabel}</span>}
    </Panel>
  );
};

export const TeamAcceptWorkspaceRow = TypedMemo(TeamAcceptWorkspaceRowComponent);
